import React, { useEffect, useState, useRef } from 'react';
import { Select, TextFieldProps } from 'mui-rff';
import { InputAdornment, createTheme, Button } from '@material-ui/core';
import ExpandMoreOutlinedIcon from '@material-ui/icons/ExpandMoreOutlined';
import Close from '@material-ui/icons/Close';
import { ThemeProvider } from '@material-ui/core/styles';
import Loader from 'ui/Loader/Loader';
import { neverShowError } from 'utils/validation';
import Input from '../Input';
import styles from './InputWithSelect.module.css';

type Props = {
  isEmpty: boolean;
  onSelectItemChange?: (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement, Event>) => void;
  onClear?: () => void;
  isLoading?: boolean;
  showMessage?: boolean;
  message?: string;
};

const InputWithSelect = ({
  name,
  children,
  isEmpty,
  onSelectItemChange,
  onClear,
  onBlur,
  isLoading,
  showMessage,
  message,
  ...props
}: TextFieldProps & Props) => {
  const [focused, setFocused] = useState(false);
  const [coordinates, setCoordinates] = useState<DOMRect | Record<string, never>>({});
  const inputRef = useRef<HTMLElement>(null);

  const updateInputCoordinates = () => {
    const inputElement = inputRef.current;
    if (inputElement) {
      setCoordinates(inputElement.getBoundingClientRect());
    }
  };

  useEffect(() => {
    window.addEventListener('resize', updateInputCoordinates);

    return function cleanup() {
      window.removeEventListener('resize', updateInputCoordinates);
    };
  }, []);

  const handleOnFocus = () => {
    setFocused(true);
  };

  const handleOnBlur = (event) => {
    setFocused(false);
    if (onBlur) {
      onBlur(event);
    }
  };

  const materialTheme = createTheme({
    palette: {
      primary: {
        main: '#4F2651',
      },
    },
    overrides: {
      MuiMenu: {
        paper: {
          // 105 = innerSelectWidth + paddingRight + mystical value = 93 + 12 + 10
          width: `${coordinates.width + 105 + 10}px`,
          top: `${coordinates.top + 64}px !important`,
          left: `${coordinates.left}px !important`,
          maxHeight: '300px',
          overflowY: 'auto',
        },
      },
      MuiFormLabel: {
        root: {
          display: 'flex',
          alignItems: 'center',
          fontFamily: 'var(--ff-etelka-text)',
          fontSize: 14,
          lineHeight: '20px',
          color: '#7D8598',
          transform: 'translate(16px, 16px)',
          MuiInputLabel: {
            shrink: {
              fontSize: '10px',
              lineHeight: '12px',
              transform: 'translate(16px, 8px)',
            },
          },
          '&.Mui-focused': {
            color: '#7D8598',
          },
          '&.Mui-error': {
            color: '#7D8598',
          },
        },
      },
      MuiFilledInput: {
        root: {
          backgroundColor: '#F6F7F9',
          cursor: 'pointer',
          '&:hover, &.Mui-focused': {
            backgroundColor: '#F6F7F9',
          },
        },
      },
      MuiInputBase: {
        root: {
          borderRadius: '4px',
          backgroundColor: '#F6F7F9',
          border: '1px solid #F6F7F9',
          '&:hover': {
            border: '1px solid #BBBFC9',
            backgroundColor: '#F6F7F9',
          },
          '&.Mui-error': {
            borderColor: '#F44336',
          },
        },
        input: {
          padding: '28px 16px 8px',
          borderRadius: 'inherit',
          height: 'fit-content',
          color: '#222736',
          fontSize: '14px',
          lineHeight: '16px',
          fontFamily: 'var(--ff-etelka-text)',
        },
      },
      MuiOutlinedInput: {
        root: {
          width: 93,
          height: 36,
          background: '#EDE9EE',
          borderRadius: '6px',
          '&:hover, &.Mui-focused': {
            border: 'unset',
            backgroundColor: '#EDE9EE',
          },
        },
      },
      MuiSelect: {
        root: {
          padding: 0,
          background: '#EDE9EE',
          '&:focused': {
            background: '#EDE9EE',
            padding: 0,
          },
        },
        icon: {
          color: '#4F2651',
          fontSize: 14,
          top: '10px',
          right: '8px',
        },
        select: {
          '&:focus': {
            backgroundColor: '#EDE9EE',
          },
        },
        nativeInput: {
          width: '61px',
          opacity: 1,
          padding: 0,
          background: '#EDE9EE',
          color: '#4F2651',
          border: 'none',
          fontSize: '12px',
          fontFamily: 'var(--ff-etelka-text)',
          height: '14px',
          top: '10px',
          bottom: '10px',
          left: '8px',
        },
      } as any,
      MuiList: {
        padding: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
      MuiInputLabel: {
        filled: {
          transform: 'translate(16px, 16px)',
        },
      },
      MuiFormHelperText: {
        root: {
          '&.Mui-error': {
            fontFamily: 'var(--ff-etelka-text)',
            color: '#EE312A',
            margin: '4px 0 0 16px',
            fontSize: '10px',
          },
        },
      },
    },
  });

  return (
    <div>
      <ThemeProvider theme={materialTheme}>
        <Input
          name={name}
          InputProps={{
            disableUnderline: true,
            endAdornment: (
              <InputAdornment position="end">
                <Select
                  name={name}
                  variant="outlined"
                  value="Beneficiary"
                  IconComponent={ExpandMoreOutlinedIcon}
                  inputProps={{ onChange: onSelectItemChange }}
                  showError={neverShowError}
                  onFocusCapture={updateInputCoordinates}
                >
                  {children}
                </Select>
              </InputAdornment>
            ),
          }}
          InputLabelProps={{ shrink: !isEmpty || focused }}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          inputRef={inputRef}
          {...props}
        />
      </ThemeProvider>
      {isLoading && (
        <div className={styles.lookingWrap}>
          <Loader size={20} />
        </div>
      )}
      {!isLoading && showMessage && (
        <div className={styles.message}>
          <div>{message || 'Beneficiary not found'}</div>
          <Button color="primary" size="small" className={styles.closeBtn} endIcon={<Close />} onClick={onClear} />
        </div>
      )}
    </div>
  );
};

export default InputWithSelect;
